import {MatSnackBar} from "@angular/material/snack-bar";
import {Ic} from "./models/common";
import {ISpec} from "./models/ISpec";
import {Observable, of} from "rxjs";
import {Utils} from "./common/utils";
import {catchError, flatMap, map, tap, timeout} from "rxjs/operators";
import {XmlUtils} from "./common/xmlUtils";
import * as R from "ramda";
import {HttpClient} from "@angular/common/http";
import {TranslateService} from "@ngx-translate/core";

export function edsTestEnabled() {
  const _eds_test = localStorage.getItem("__eds_test__");
  try {

    return _eds_test.trim().toLocaleLowerCase() === 'true';
  } catch (e) {
    return false;
  }
}

export function copyToClipboard(txt: string, snack: MatSnackBar) {
  const unsuportClipboardCopy = (t, s) => {
    const copyText = document.getElementById("_copyURL_text_");
    copyText.setAttribute('value', t);
    copyText.style.visibility = "visible";

    /* Select the text field */
    // @ts-ignore
    copyText.select();
    // @ts-ignore
    copyText.setSelectionRange(0, 99999); /*For mobile devices*/

    /* Copy the text inside the text field */
    document.execCommand("copy");
    copyText.style.visibility = "hidden";
    if (s) {
      s.open('URL copied', 'OK', {duration: 2500});
    }
  };

  if (navigator.clipboard) {
    navigator.clipboard.writeText(txt)
      .then(() => {
        snack.open('URL Copied', 'OK', {duration: 2500});
      })
      .catch((error) => {
        unsuportClipboardCopy(txt, snack);
      });
  } else {
    unsuportClipboardCopy(txt, snack);
  }
}

export function shareText(txt: string, snack: MatSnackBar) {
  // @ts-ignore
  if (navigator.share) {
    // @ts-ignore
    navigator.share({
      title: 'eDesignSuite',
      url: txt,
    })
      .then(() => {
        snack.open('URL shared', 'OK', {duration: 2500});
      })
      .catch((error) => {
        copyToClipboard(txt, snack);
      });
  } else {
    copyToClipboard(txt, snack);
  }
}

/*
* Requested by olm for tracking
* */
export function setWindowTitle(catalog?: string, translateService?: TranslateService) {
  const baseTitle = 'eDesignSuite | Design & Simulation Tools | STMicroelectronics';
  let startTitle =  '';
  if (catalog) {
    switch (catalog) {
      case 'dcdc':
        startTitle = 'Converter DCDC | ';
        break;
      case 'acdc':
        startTitle = 'Converter ACDC | ';
        break;
      case 'led':
        startTitle = 'LED DCDC | ';
        break;
      case 'led_acdc':
        startTitle = 'LED ACDC | ';
        break;
      case 'digital_power':
        startTitle = 'STM32 Digital Power | ';
        break;
    }
  }

  if(!window.location.hash.includes("hackathon")){
    document.title = translateService
      ? `${catalog ? translateService.instant(`COMMON.documentTitles.${catalog}`) : ''}${translateService.instant('COMMON.documentTitles.baseTitle')}`
      : `${startTitle}${baseTitle}`;
  } else {
    document.title = "BIG HACK 2023";
  }

}

export function getSoluctionEfficiency(http: HttpClient, appName: string, ic: Ic, spec: ISpec<number>): Observable<Ic> {
  const app = appName.toLowerCase().includes('led_acdc') ? 'acled' : appName.toLowerCase();
  const url = `/api/${app}/top/${ic.topology.toLowerCase()}/ic/${ic.CPN.toLowerCase()}/projects`;

  const _getVinSpec = () => {
    return {
      min: spec.input.min,
      max: spec.input.max,
      freq: (app.includes('acdc') || app.includes('acled')) ? spec.input.freq : undefined
    };
  };

  const _getVoutSpec = () => {
    return app.includes('led') ? undefined : spec.output.outputs.map(x => ({
      v: Math.abs(x.voltage),
      r: (ic.key && ic.key.search(/:pfc/i) >= 0) ? undefined : 2,     // if pfc in key ripple undefined to take the default value else 2%
      i: x.current
    }));
  };

  const _getLedSpec = () => {
    return !app.includes('led') ? undefined : ({
      VF: Math.abs(spec.output.outputs[0].voltage),
      Rd: spec.output.ledParam.rd,
      IF: spec.output.outputs[0].voltage
    });
  };

  const _getNumLedSpec = () => {
    return !app.includes('led') ? undefined : ({
      serie: spec.output.ledParam.serial,
      parallel: spec.output.ledParam.parallel,
    });
  };

  const body = {
    extra_info: {},
    ic: ic.key,
    vIn: _getVinSpec(),
    outs: _getVoutSpec(),
    led: _getLedSpec(),
    numLed: _getNumLedSpec()
  };
  const setEffState = (state: string) => R.assoc('efficiencyState', state);
  const setEffValue = (eff: number) => R.assoc('efficiency', eff);
  const hasEffValue = (response) => R.hasPath(['analysis', 'efficiency', 'value', 'efficiency'], response);
  const getEffValue = (response) => R.path(['analysis', 'efficiency', 'value', 'efficiency'], response) as number * 100;
  const updateEff = (eff: number) => R.pipe(setEffValue(eff), setEffState('calculated'));

  const startAvailable = ic.app_name && ic.app_name.endsWith('_html') && spec.output.outputs.length >= Utils.getFieldValue(ic, 'designData.minNumOutput', 1);

  if (!startAvailable) {
    return of(setEffState('default')(ic));
  }

  return http.post(url, JSON.stringify(body), {responseType: 'text'}).pipe(
    // tap((x) => console.log('LoadXmlFile form url', fileName, x)),
    timeout(20000),
    XmlUtils.toJSON,
    // tap(x => console.log('_getSoluctionEfficiency', x)),
    // map(response => R.assoc('efficiency', (R.pathOr(ic.efficiency / 100, ['analysis', 'efficiency', 'value', 'efficiency'], response) as number) * 100, ic)),
    map(response => hasEffValue(response) ? updateEff(getEffValue(response))(ic) : setEffState('estimated')(ic)),
    // tap(x => console.log('_getSoluctionEfficiency Value', x)),
    catchError((x) => {
      // console.log('_getSoluctionEfficiency Error', x);
      return of(setEffState('estimated')(ic));
    })
  );
}
